import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import LoadingSpinner from './LoadingSpinner';
import SectionHeader from './SectionHeader';
import './ContractEmails.css';
import { getGmailApiUrl } from '../utils/apiUrls';

const GMAIL_URL = getGmailApiUrl();

const ReferralAgreementEmails = ({ referralAgreementId, userEmail, isExpanded = false, onToggle }) => {
  const [emails, setEmails] = useState([]);
  const [filteredEmails, setFilteredEmails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [expandedBodies, setExpandedBodies] = useState(new Set());
  const [totalFound, setTotalFound] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [offset, setOffset] = useState(0);
  const offsetRef = useRef(0);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredEmails(emails);
    } else {
      const q = searchQuery.toLowerCase();
      setFilteredEmails(emails.filter((email) =>
        [email.from, email.from_email, email.to, email.cc, email.subject, email.body]
          .join(' ').toLowerCase().includes(q)
      ));
    }
  }, [emails, searchQuery]);

  const fetchEmails = async (reset = true) => {
    if (!referralAgreementId || !userEmail) return;
    try {
      if (reset) { setLoading(true); setOffset(0); offsetRef.current = 0; setEmails([]); }
      else { setLoadingMore(true); }
      setError(null);

      const currentOffset = reset ? 0 : offsetRef.current;
      const response = await axios.get(`${GMAIL_URL}/emails`, {
        params: {
          user_email: userEmail,
          referral_agreement_id: referralAgreementId,
          limit: 10,
          offset: currentOffset,
        },
      });

      if (response.data.status === 'success') {
        const newEmails = response.data.emails || response.data.results || [];
        setEmails((prev) => (reset ? newEmails : [...prev, ...newEmails]));
        const nextOffset = currentOffset + newEmails.length;
        offsetRef.current = nextOffset;
        setOffset(nextOffset);
        setTotalFound(response.data.total_found || response.data.total_accessible || nextOffset);
        setHasMore(response.data.has_more ?? newEmails.length >= 10);
      } else {
        setError('Failed to fetch emails');
      }
    } catch (err) {
      if (err.code === 'ERR_NETWORK') {
        setError('Unable to connect to email service.');
      } else if (err.response?.status === 404) {
        setError('No emails found.');
      } else {
        setError('Failed to load emails. Please try again.');
      }
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    if (referralAgreementId && userEmail) fetchEmails(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [referralAgreementId, userEmail]);

  const formatDate = (ts) => {
    try {
      return new Date(ts).toLocaleDateString('en-US', {
        year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
      });
    } catch { return 'Invalid date'; }
  };

  const toggleBody = (id) => {
    setExpandedBodies((prev) => {
      const s = new Set(prev);
      if (s.has(id)) s.delete(id);
      else s.add(id);
      return s;
    });
  };

  const handleScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    if ((scrollTop + clientHeight) / scrollHeight > 0.8 && hasMore && !loadingMore) {
      fetchEmails(false);
    }
  };

  if (loading) {
    return (
      <div className="emails-section">
        <SectionHeader title="Email History" isExpanded={isExpanded} onToggle={onToggle} />
        <div className="emails-content"><LoadingSpinner /></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="emails-section">
        <SectionHeader title="Email History" isExpanded={isExpanded} onToggle={onToggle} />
        <div className="emails-content">
          <div className="emails-error">
            <p>{error}</p>
            <button type="button" onClick={() => fetchEmails(true)} className="retry-button">Retry</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="emails-section">
      <SectionHeader title="Email History" isExpanded={isExpanded} onToggle={onToggle}>
        <div className="search-container" onClick={(e) => e.stopPropagation()}>
          <input
            type="text"
            placeholder="Search emails..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="search-input"
          />
        </div>
      </SectionHeader>

      {isExpanded && (
        <div className="emails-content">
          {emails.length === 0 ? (
            <div className="no-emails"><p>No emails found for this referral agreement.</p></div>
          ) : filteredEmails.length === 0 ? (
            <div className="no-emails"><p>No emails match your search criteria.</p></div>
          ) : (
            <div className="emails-list" onScroll={handleScroll} style={{ maxHeight: '600px', overflowY: 'auto' }}>
              {filteredEmails.map((email, index) => {
                const emailId = email.db_id || email.id || `email-${index}`;
                const isBodyExpanded = expandedBodies.has(emailId);
                const shouldTruncate = email.body && email.body.length > 200;

                return (
                  <div key={emailId} className="email-item">
                    <div className="email-header">
                      <div className="email-sender">
                        <span className="from-label">From:</span>{' '}
                        {email.from && email.from !== 'N/A'
                          ? <><strong>{email.from}</strong> {email.from_email}</>
                          : email.from_email}
                      </div>
                      <div className="email-date">{formatDate(email.timestamp)}</div>
                    </div>
                    <div className="email-to">
                      <span className="to-label">To:</span>{' '}
                      {email.to && email.to !== 'N/A' ? <>{email.to} {email.to_email}</> : email.to_email}
                      {email.cc && <span className="cc-info"> | CC: {email.cc}</span>}
                    </div>
                    <div className="email-subject"><strong>{email.subject || 'No Subject'}</strong></div>
                    {email.body && (
                      <div className="email-body">
                        {shouldTruncate && !isBodyExpanded
                          ? <>{email.body.substring(0, 200)}... <button type="button" className="expand-btn" onClick={() => toggleBody(emailId)}>Show more</button></>
                          : <>{email.body}{shouldTruncate && <> <button type="button" className="expand-btn" onClick={() => toggleBody(emailId)}>Show less</button></>}</>
                        }
                      </div>
                    )}
                  </div>
                );
              })}
              {loadingMore && <div style={{ textAlign: 'center', padding: '1rem' }}>Loading more...</div>}
              {!hasMore && emails.length > 0 && (
                <div style={{ textAlign: 'center', padding: '1rem', color: '#999', fontSize: '0.85rem' }}>
                  {totalFound} email{totalFound !== 1 ? 's' : ''} total
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ReferralAgreementEmails;
